import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DashboardLayout, AuthLayout } from '@/layouts';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { LoadingSpinner, ToastNotification, ErrorBoundary } from '@/components/ui';
import { useTheme } from '@/hooks';
import { ROUTES } from '@/utils/constants';
import React from 'react';

// Lazy load pages for code splitting
const Login = React.lazy(() => import('@/pages/auth/Login'));
const VerifyEmail = React.lazy(() => import('@/pages/auth/VerifyEmail'));
const ForgotPassword = React.lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('@/pages/auth/ResetPassword'));
const AcceptInvitation = React.lazy(() => import('@/pages/auth/AcceptInvitation'));
const DashboardHome = React.lazy(() => import('@/pages/dashboard/Home'));
const DetectionsList = React.lazy(() => import('@/pages/detections/List'));
const DetectionDetail = React.lazy(() => import('@/pages/detections/Detail'));
const TakedownsList = React.lazy(() => import('@/pages/takedowns/List'));
const TakedownDetail = React.lazy(() => import('@/pages/takedowns/Detail'));
const CampaignsList = React.lazy(() => import('@/pages/campaigns/List'));
const CampaignDetail = React.lazy(() => import('@/pages/campaigns/Detail'));
const BotnetsList = React.lazy(() => import('@/pages/botnets/List'));
const BotnetDossier = React.lazy(() => import('@/pages/botnets/Detail'));
const KitsList = React.lazy(() => import('@/pages/kits/List'));
const KitDossier = React.lazy(() => import('@/pages/kits/Detail'));
const IntelligenceOverview = React.lazy(() => import('@/pages/intelligence/Overview'));
const ActorsList = React.lazy(() => import('@/pages/actors/List'));
const ActorDossier = React.lazy(() => import('@/pages/actors/Detail'));
const DMARCReports = React.lazy(() => import('@/pages/dmarc/Reports'));
const DMARCAnalysis = React.lazy(() => import('@/pages/dmarc/Analysis'));
const UnauthorizedSources = React.lazy(() => import('@/pages/dmarc/UnauthorizedSources'));
const HoneytokenDetail = React.lazy(() => import('@/pages/honeytokens/Detail'));
const HoneytokenAnalytics = React.lazy(() => import('@/pages/honeytokens/Analytics'));
const TrapsPage = React.lazy(() => import('@/pages/traps'));
const UsersList = React.lazy(() => import('@/pages/users/List'));
const UserDetail = React.lazy(() => import('@/pages/users/Detail'));
const CompanySettings = React.lazy(() => import('@/pages/company/Settings'));
const SsoSettings = React.lazy(() => import('@/pages/company/SsoSettings'));
const ScimSettings = React.lazy(() => import('@/pages/company/ScimSettings'));
const IntegrationsList = React.lazy(() => import('@/pages/integrations/List'));
const OrgApiCredentials = React.lazy(() => import('@/pages/company/ApiCredentials'));
const Profile = React.lazy(() => import('@/pages/profile/Profile'));
const ProfileApiCredentials = React.lazy(() => import('@/pages/profile/ApiCredentials'));
const OnboardingTour = React.lazy(() => import('@/pages/onboarding/OnboardingTour'));
const ServicesPage = React.lazy(() => import('@/pages/services'));
const BookingsList = React.lazy(() => import('@/pages/services/bookings/List'));
const BookingDetail = React.lazy(() => import('@/pages/services/bookings/Detail'));
const GDPRSettings = React.lazy(() => import('@/pages/settings/GDPRSettings'));
const CompanyGDPRSettings = React.lazy(() => import('@/pages/settings/CompanyGDPRSettings'));

// Admin Interface pages (Platform Admin only)
const AdminLayout = React.lazy(() => import('@/components/admin/AdminLayout'));
const AdminCompanies = React.lazy(() => import('@/pages/admin/companies'));
const AdminCompanyDetail = React.lazy(() => import('@/pages/admin/companies/[id]'));
const AdminCompanyCreate = React.lazy(() => import('@/pages/admin/companies/create'));
const AdminCompanyBatch = React.lazy(() => import('@/pages/admin/companies/batch'));
const AdminBookings = React.lazy(() => import('@/pages/admin/bookings'));
const AdminBookingDetail = React.lazy(() => import('@/pages/admin/bookings/[id]'));
const AdminQuotes = React.lazy(() => import('@/pages/admin/quotes'));
const AdminServices = React.lazy(() => import('@/pages/admin/services'));
const AdminAuditLogs = React.lazy(() => import('@/pages/admin/audit-logs'));
const AdminMailSettings = React.lazy(() => import('@/pages/admin/mail-settings'));
const AdminDMARCSettings = React.lazy(() => import('@/pages/admin/dmarc-settings'));
const AdminRegistrarConfigs = React.lazy(() => import('@/pages/admin/registrar-configs'));
const AdminInstitutionalContacts = React.lazy(() => import('@/pages/admin/institutional-contacts'));
const AdminHoneypots = React.lazy(() => import('@/pages/admin/honeypots'));
const AdminKitSatellites = React.lazy(() => import('@/pages/admin/kit-satellites'));
const AdminResearchKeys = React.lazy(() => import('@/pages/admin/research-keys'));
const AdminProxies = React.lazy(() => import('@/pages/admin/proxies'));

// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <LoadingSpinner size="lg" />
    </div>
  );
}

function App() {
  // Initialize theme management (syncs Zustand state with document class)
  useTheme();

  return (
    <ErrorBoundary>
      <Routes>
        {/* Public routes */}
        <Route element={<AuthLayout />}>
          <Route
            path={ROUTES.LOGIN}
            element={
              <Suspense fallback={<PageLoader />}>
                <Login />
              </Suspense>
            }
          />
          {/* Closed alpha — there is no public signup. The route stays so bookmarks and
              old links land somewhere sensible instead of the 404 page. `pages/auth/
              Register` and `components/forms/RegisterForm` are kept, unrouted, so public
              launch is this route plus the API's ALLOW_PUBLIC_REGISTRATION flag. */}
          <Route path={ROUTES.REGISTER} element={<Navigate to={ROUTES.LOGIN} replace />} />
          <Route
            path={ROUTES.VERIFY_EMAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <VerifyEmail />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.FORGOT_PASSWORD}
            element={
              <Suspense fallback={<PageLoader />}>
                <ForgotPassword />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.RESET_PASSWORD}
            element={
              <Suspense fallback={<PageLoader />}>
                <ResetPassword />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ACCEPT_INVITATION}
            element={
              <Suspense fallback={<PageLoader />}>
                <AcceptInvitation />
              </Suspense>
            }
          />
        </Route>

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard */}
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <Suspense fallback={<PageLoader />}>
                <DashboardHome />
              </Suspense>
            }
          />

          {/* Detections */}
          <Route
            path={ROUTES.DETECTIONS}
            element={
              <Suspense fallback={<PageLoader />}>
                <DetectionsList />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.DETECTION_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <DetectionDetail />
              </Suspense>
            }
          />

          {/* Takedowns */}
          <Route
            path={ROUTES.TAKEDOWNS}
            element={
              <Suspense fallback={<PageLoader />}>
                <TakedownsList />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.TAKEDOWN_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <TakedownDetail />
              </Suspense>
            }
          />

          {/* ── Intelligence ──────────────────────────────────────
              Flat routes: every list shares a prefix with its dossier, so leaving a
              list doesn't drop you out of a container and breadcrumbs stay honest.
              /intelligence is an Overview page showing the axes at once — the thing a
              tab strip could never do, since it shows one axis and hides the rest.

              Campaigns sits at top level (its own sidebar entry); Botnets, Actors and
              Kits sit behind the Intelligence group. A tenant also meets its own kits as
              `kit_hunter` detections — /kits is the cross-tenant corpus. */}
          <Route
            path={ROUTES.INTELLIGENCE}
            element={
              <Suspense fallback={<PageLoader />}>
                <IntelligenceOverview />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.CAMPAIGNS}
            element={
              <Suspense fallback={<PageLoader />}>
                <CampaignsList />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.BOTNETS}
            element={
              <Suspense fallback={<PageLoader />}>
                <BotnetsList />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.KITS}
            element={
              <Suspense fallback={<PageLoader />}>
                <KitsList />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ACTORS}
            element={
              <Suspense fallback={<PageLoader />}>
                <ActorsList />
              </Suspense>
            }
          />

          {/* Hub-era tab paths + the retired shared-files page → flat routes */}
          <Route path={ROUTES.LEGACY_HUB_CAMPAIGNS} element={<Navigate to={ROUTES.CAMPAIGNS} replace />} />
          <Route path={ROUTES.LEGACY_HUB_BOTNETS} element={<Navigate to={ROUTES.BOTNETS} replace />} />
          <Route path={ROUTES.LEGACY_HUB_KIT_SHARED_FILES} element={<Navigate to={ROUTES.KITS} replace />} />
          <Route path={ROUTES.LEGACY_HUB_KITS} element={<Navigate to={ROUTES.KITS} replace />} />
          <Route path={ROUTES.LEGACY_HUB_ACTORS} element={<Navigate to={ROUTES.ACTORS} replace />} />
          <Route path={ROUTES.LEGACY_KIT_SHARED_FILES} element={<Navigate to={ROUTES.KITS} replace />} />

          {/* Intelligence dossiers (top-level; reached from anywhere) */}
          <Route
            path={ROUTES.CAMPAIGN_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <CampaignDetail />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.BOTNET_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <BotnetDossier />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.KIT_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <KitDossier />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ACTOR_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <ActorDossier />
              </Suspense>
            }
          />

          {/* DMARC */}
          <Route
            path={ROUTES.DMARC_REPORTS}
            element={
              <Suspense fallback={<PageLoader />}>
                <DMARCReports />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.DMARC_ANALYSIS}
            element={
              <Suspense fallback={<PageLoader />}>
                <DMARCAnalysis />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.DMARC_UNAUTHORIZED}
            element={
              <Suspense fallback={<PageLoader />}>
                <UnauthorizedSources />
              </Suspense>
            }
          />

          {/* Honeytokens */}
          <Route
            path={ROUTES.TRAPS}
            element={
              <Suspense fallback={<PageLoader />}>
                <TrapsPage />
              </Suspense>
            }
          />

          {/* Honeytoken sub-routes */}
          <Route
            path={ROUTES.HONEYTOKEN_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <HoneytokenDetail />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.HONEYTOKEN_ANALYTICS}
            element={
              <Suspense fallback={<PageLoader />}>
                <HoneytokenAnalytics />
              </Suspense>
            }
          />

          {/* Legacy routes - redirect to the Honeytokens page */}
          <Route
            path={ROUTES.HONEYPOTS}
            element={<Navigate to={ROUTES.TRAPS} replace />}
          />
          <Route
            path={ROUTES.HONEYTOKENS}
            element={<Navigate to={ROUTES.TRAPS} replace />}
          />

          {/* Users (Admin) */}
          <Route
            path={ROUTES.USERS}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <UsersList />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.USER_DETAIL}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <UserDetail />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Company Settings */}
          <Route
            path={ROUTES.COMPANY_SETTINGS}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <CompanySettings />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* SSO Settings (Admin) */}
          <Route
            path={ROUTES.COMPANY_SSO}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <SsoSettings />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* SCIM Settings (Admin) */}
          <Route
            path={ROUTES.COMPANY_SCIM}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <ScimSettings />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Integrations (Admin) */}
          <Route
            path={ROUTES.COMPANY_INTEGRATIONS}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <IntegrationsList />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Organization API Credentials (Admin) */}
          <Route
            path={ROUTES.COMPANY_API_CREDENTIALS}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <OrgApiCredentials />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Profile */}
          <Route
            path={ROUTES.PROFILE}
            element={
              <Suspense fallback={<PageLoader />}>
                <Profile />
              </Suspense>
            }
          />

          {/* Profile API Credentials */}
          <Route
            path={ROUTES.PROFILE_API_CREDENTIALS}
            element={
              <Suspense fallback={<PageLoader />}>
                <ProfileApiCredentials />
              </Suspense>
            }
          />

          {/* GDPR Settings (Privacy & Data) */}
          <Route
            path={ROUTES.SETTINGS_GDPR}
            element={
              <Suspense fallback={<PageLoader />}>
                <GDPRSettings />
              </Suspense>
            }
          />

          {/* Company GDPR Settings (Admin only) */}
          <Route
            path={ROUTES.COMPANY_GDPR_SETTINGS}
            element={
              <ProtectedRoute requiredRole="admin">
                <Suspense fallback={<PageLoader />}>
                  <CompanyGDPRSettings />
                </Suspense>
              </ProtectedRoute>
            }
          />

          {/* Onboarding */}
          <Route
            path={ROUTES.ONBOARDING}
            element={
              <Suspense fallback={<PageLoader />}>
                <OnboardingTour />
              </Suspense>
            }
          />

          {/* Premium Services */}
          <Route
            path={ROUTES.SERVICES}
            element={
              <Suspense fallback={<PageLoader />}>
                <ServicesPage />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.SERVICES_BOOKINGS}
            element={
              <Suspense fallback={<PageLoader />}>
                <BookingsList />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.SERVICES_BOOKING_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <BookingDetail />
              </Suspense>
            }
          />
        </Route>

        {/* Admin Interface routes (Platform Admin only) */}
        <Route
          element={
            <ProtectedRoute requiredRole="platform_admin">
              <Suspense fallback={<PageLoader />}>
                <AdminLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route
            path={ROUTES.ADMIN_COMPANIES}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminCompanies />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_COMPANY_CREATE}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminCompanyCreate />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_COMPANY_BATCH}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminCompanyBatch />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_COMPANY_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminCompanyDetail />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_BOOKINGS}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminBookings />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_BOOKING_DETAIL}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminBookingDetail />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_QUOTES}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminQuotes />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_SERVICES}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminServices />
              </Suspense>
            }
          />
          <Route
            path={ROUTES.ADMIN_AUDIT_LOGS}
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminAuditLogs />
              </Suspense>
            }
          />
          <Route
            path="/admin/mail-settings"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminMailSettings />
              </Suspense>
            }
          />
          <Route
            path="/admin/proxies"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminProxies />
              </Suspense>
            }
          />
          <Route
            path="/admin/dmarc-settings"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminDMARCSettings />
              </Suspense>
            }
          />
          <Route
            path="/admin/registrar-configs"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminRegistrarConfigs />
              </Suspense>
            }
          />
          <Route
            path="/admin/institutional-contacts"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminInstitutionalContacts />
              </Suspense>
            }
          />
          <Route
            path="/admin/honeypots"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminHoneypots />
              </Suspense>
            }
          />
          <Route
            path="/admin/kit-satellites"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminKitSatellites />
              </Suspense>
            }
          />
          <Route
            path="/admin/research-keys"
            element={
              <Suspense fallback={<PageLoader />}>
                <AdminResearchKeys />
              </Suspense>
            }
          />
          {/* Redirect /admin to /admin/companies */}
          <Route
            path={ROUTES.ADMIN}
            element={<Navigate to={ROUTES.ADMIN_COMPANIES} replace />}
          />
        </Route>

        {/* Redirects */}
        <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
        <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      </Routes>

      {/* Global toast notifications */}
      <ToastNotification />
    </ErrorBoundary>
  );
}

export default App;
